 <template>
  <div class="full-wrapper">
    <el-scrollbar :ref="scrollbarRef">
      <div class="full">
        <el-row class="list-info">
          <el-col :span="3" class="select">
            <el-select class="custom-input" type="medium" v-model="analysisType" @change="loadFlowChart">
              <el-option v-for="type in filterAnalysisType" :key="type.value" :label="type.label" :value="type.value" />
            </el-select>
          </el-col>
          <el-col :span="17">
            <ul class="lists">
              <li>
                <span class="title">기간</span>
                <span class="high">
                  {{ getDateRange() }}
                </span>
              </li>
              <li>
                <span class="title">단위</span>
              </li>
              <li style="background: none; margin-left: 0;">
                <span class="select">
                  <el-select type="medium" style="width: 100px;" v-model="unitType" @change="loadFlowChart">
                    <el-option v-for="unit in unitList" :key="unit.value" :label="unit.label" :value="unit.value" />
                  </el-select>
                </span>
              </li>
              <li>
                <span class="title">방향</span>
                <span class="high">
                  IN <el-switch v-model="direction" inactive-color="#409EFF" active-value="output" inactive-value="input" @change="loadFlowChart" /> OUT
                </span>
              </li>
            </ul>
          </el-col>
          <el-col :span="4">
            <el-button type="content" size="medium"  class="customised-icon refresh-icon" style="float:right" @click="onClickRefreshButton"></el-button>
            <el-button type="content" size="medium"  class="customised-icon flowmap mr-r4" style="float:right" @click="onFlowMapButtonClick"></el-button>

            <!-- <el-button type="no-border" size="large" style="float:right" @click="onClickRefreshButton"><i class="far fa-undo" /></el-button>
            <el-button type="no-border" size="large"  style="float:right" @click="onFlowMapButtonClick"><i class="fab fa-hubspot" /></el-button> -->
          </el-col>
        </el-row>
        <!-- 추이차트 -->
        <w-flow-chart class="mr-b30" ref="flowChart" :series="seriesData" :unit-type="unitType" @loadSmartTrace="setSmartTraceParams"/>

        <!-- 스마트 추적 -->
        <w-smart-trace-view class="mr-b30" :analysis-type="analysisType" :flow-params="smartTraceParams" @setSmartTraceFilter="setSmartTraceFilter"/>

        <!-- 트래픽 목록 -->
        <w-traffic-list-view :flow-params="trafficParams"></w-traffic-list-view>

        <!-- Flow Map 분석 팝업창 -->
        <w-flow-map-analysis-popup :analysis-type="analysisType" :flow-map-params="flowMapParams" ref="flowMapAnalysisPopup"/>
      </div>
    </el-scrollbar>
    <w-scroll-top-button :visible="enableScollTop" @move-scroll-top="moveScrollTop"/>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import clone from 'lodash/clone';
import filter from 'lodash/filter';
import find from 'lodash/find';
import isNil from 'lodash/isNil';
import ScrollbarMixin from '@/mixins/scrollbar';
import { isEmpty } from '@/utils/basic';
import MsgUtil from '@/utils/message';
import FlowAPI from '@/api/performance/flow-analysis/flow-analysis';
import WFlowMapAnalysisPopup from '../popup/FlowMapAnalysisPopup';
import WFlowChart from '../chart/FlowChart';
import WSmartTraceView from './SmartTraceView';
import WTrafficListView from './TrafficListView';

export default {
  name: 'WFlowAnalysisView',
  mixins: [ScrollbarMixin],
  components: {
    WFlowMapAnalysisPopup,
    WFlowChart,
    WSmartTraceView,
    WTrafficListView,
  },
  props: {
    /**
     * 좌측 트리 노드 선택시 > netdevice, netinterface
     * root 선택시 > null
     * @type {[type]}
     */
    objType: String,
    flowObjFilter: Object,
  },
  data() {
    return {
      analysisTypeList: [
        { label: '인터페이스별 분석', value: 'netinterface', netdevice: true },
        { label: '장비별 분석', value: 'netdevice' },
        { label: 'IP별 분석', value: 'ipaddress' },
        { label: '프로토콜별 분석', value: 'protocol', netdevice: true, netinterface: true },
        { label: '서비스포트별 분석', value: 'port', netdevice: true, netinterface: true },
        { label: 'IP 그룹별 분석', value: 'ipaddressGrp', netdevice: true, netinterface: true },
        { label: '인터페이스 그룹별 분석', value: 'netinterfaceGrp' },
      ],
      analysisType: 'netinterface',
      unitList: [{ label: 'Bytes', value: 'octets' }, { label: 'Packets', value: 'packets' }, { label: 'bps', value: 'bps' }, { label: 'pps', value: 'pps' }],
      unitType: 'octets',
      direction: 'input',
      seriesData: [],
      smartTraceParams: {},
      flowMapParams: {},
      analysisFilterName: '',
      /**
       * 관리대상
       * @type {Object}
       */
      basicFilter: null,
      trafficParams: {},
    };
  },
  computed: {
    /**
     * 추이 차트의 방향 컴포넌트 표현 여부
     * @return {[type]} [description]
     * @author jda
     */
    showDirection() {
      const type = this.analysisType;
      return type !== 'netdevice' && type !== 'protocol';
    },
    /**
     * 전체, 장비, 인터페이스 선택시 분석 타입 필터링
     * @return {Boolean} [description]
     * @author jda
     */
    filterAnalysisType() {
      const type = this.objType;
      const analysisTypeList = clone(this.analysisTypeList);

      if (isEmpty(type)) {
        return analysisTypeList;
      }
      return filter(analysisTypeList, type);
    },
    /**
     * 추이 그래프 데이터를 조회하기 위해 파라메터를 가공한다.
     * 좌측 트리 선택여부, 조회 기간, 분석 타입, 단위, 방향
     * order: desc, count: 8이 default이다.
     * @return {[type]} [description]
     * @author jda
     */
    flowChartParams() {
      const type = this.objType;
      const analysisDateParams = this.analysisDateParams;
      const analysisType = this.analysisType;
      const params = {
        analysisType,
        direction: this.direction,
        requestParamVO: {
          basicFilter: this.basicFilter,
          startDateTime: analysisDateParams.startDatetime,
          endDateTime: analysisDateParams.endDatetime,
          meter: this.unitType,
        },
      };

      if (analysisType === 'netinterfaceGrp' || analysisType === 'ipaddressGrp') {
        Object.assign(params.requestParamVO, { groupFilter: this.createGroupFilter() });
      } else {
        Object.assign(params.requestParamVO, { analysisFilter: this.createAnalysisFilter() });
      }

      if (!isEmpty(this.selectedNode) && !isEmpty(type)) {
        const obj = this.selectedNode.obj;
        Object.assign(params, { objId: obj.objId, objDefineId: obj.objDefineId });
      }

      return params;
    },
    ...mapState({ analysisDateData: (state) => state.performance.analysisDateData }),
    ...mapGetters(['analysisDateParams', 'selectedNode']),
  },
  watch: {
    filterAnalysisType: {
      immediate: true,
      handler(typeList) {
        if (isEmpty(typeList)) {
          this.analysisType = 'netinterface';
        } else {
          this.analysisType = this.filterAnalysisType[0].value;
        }
      },
    },
    /**
     * 노드 > Flow분석에서 조회해온 관리대상을 바인딩한 후, 차트를 조회한다.
     * @param  {[type]} basicFilter [description]
     * @return {[type]}             [description]
     * @author jda
     */
    flowObjFilter(basicFilter) {
      this.basicFilter = basicFilter;
      this.loadFlowChart();
    },
  },
  mounted() {
    this.setScrollData();
  },
  methods: {
    getDateRange() {
      if (!isNil(this.analysisDateData)) {
        return `${this.analysisDateData.data.startLabel} ~ ${this.analysisDateData.data.endLabel}`;
      }
      return '-';
    },
    /**
     * Elasticsearch의 관리대상 파라메터를 바인딩한다.
     * @param  {[type]} reload [description]
     * @author jda
     */
    setBasicFilter(reload) {
      const treeType = this.$store.state.router.treeType;
      const params = {
        treeType,
      };
      if (!isEmpty(this.objType)) {
        const obj = this.selectedNode.obj;
        Object.assign(params, { objId: obj.objId, objDefineId: obj.objDefineId });
      }

      FlowAPI.getBasicFilter(params)
        .then((response) => {
          if (response.data.success) {
            this.basicFilter = response.data.data;

            if (reload) {
              this.loadFlowChart();
            }
          } else {
            this.$message(MsgUtil.getCustomResultMsgData('관리대상을 조회하지 못하였습니다.', false));
          }
        })
        .catch(() => {
          this.$message(MsgUtil.getCustomResultMsgData('관리대상을 조회하지 못하였습니다.', false));
        });
    },
    /**
     * 분석 타입별로 Flow를 조회할 파라메터를 생성한다.
     * @return {Boolean} [description]
     * @author jda
     */
    createAnalysisFilter() {
      const direction = this.direction;
      const analysisFilter = {};
      let analysisFilterName;
      switch (this.analysisType) {
        case 'netinterface':
          if (direction === 'input') {
            analysisFilterName = 'deviceInput';
          } else {
            analysisFilterName = 'deviceOutput';
          }
          break;

        case 'netdevice': {
          analysisFilterName = 'deviceIp';
          break;
        }
        case 'ipaddress':
          if (direction === 'input') {
            analysisFilterName = 'srcIp';
          } else {
            analysisFilterName = 'dstIp';
          }
          break;

        case 'protocol': {
          analysisFilterName = 'protocol';
          break;
        }
        case 'port': {
          if (direction === 'input') {
            analysisFilterName = 'protocolSrcPort';
          } else {
            analysisFilterName = 'protocolDstPort';
          }
          break;
        }
        default:
          break;
      }
      analysisFilter[analysisFilterName] = null;
      this.analysisFilterName = analysisFilterName;
      return analysisFilter;
    },
    /**
     * IP, 인터페이스 그룹별 분석에서의 groupFilter를 가공한다.
     * @return {[type]} [description]
     * @author jda
     */
    createGroupFilter() {
      const direction = this.direction;
      const groupFilter = {};
      let groupFilterName;
      switch (this.analysisType) {
        case 'netinterfaceGrp':
          if (direction === 'input') {
            groupFilterName = 'deviceInput';
          } else {
            groupFilterName = 'deviceOutput';
          }
          break;
        case 'ipaddressGrp':
          if (direction === 'input') {
            groupFilterName = 'srcIp';
          } else {
            groupFilterName = 'dstIp';
          }
          break;
        default:
          break;
      }
      groupFilter[groupFilterName] = null;
      this.analysisFilterName = groupFilterName;
      return groupFilter;
    },
    /**
     * Flow 추이 차트 데이터를 조회한다.
     * @return {[type]} [description]
     * @author jda
     */
    loadFlowChart() {
      FlowAPI.getFlowChartData(this.flowChartParams)
        .then((response) => {
          if (response.data.success) {
            const result = response.data.data;
            this.seriesData = result.seriesData;
            this.basicFilter = result.basicFilter;

            this.createFlowMapParams(result.seriesData);
          } else {
            this.$message(MsgUtil.getCustomResultMsgData('Flow 데이터를 조회하지 못하였습니다.', false));
          }
        })
        .catch(() => {
          this.$message(MsgUtil.getCustomResultMsgData('Flow 데이터를 조회하지 못하였습니다.', false));
        });
    },
    /**
     * Flow Map 분석 팝업창 open
     * @author jeee12
     */
    onFlowMapButtonClick() {
      this.$refs.flowMapAnalysisPopup.open();
    },
    /**
     * Flow Map 분석 팝업창에 필요한 파라메터 가공
     * @param  {[type]} record [description]
     * @return {[type]}        [description]
     * @author jda
     */
    createFlowMapParams(record) {
      if (isEmpty(record)) {
        return;
      }
      const analysisDateParams = this.analysisDateParams;
      const flowMapParams = {
        startDateTime: analysisDateParams.startDatetime,
        endDateTime: analysisDateParams.endDatetime,
      };

      const top1Data = record[0].info;
      if (this.analysisType === 'netinterface') {
        Object.assign(flowMapParams, { deviceIp: find(top1Data, { type: 'deviceIp' }).value, input: find(top1Data, { type: this.direction }).value });
      } else if (this.analysisType === 'netdevice') {
        Object.assign(flowMapParams, { deviceIp: find(top1Data, { type: 'deviceIp' }).value });
      }
      this.flowMapParams = flowMapParams;
    },
    /**
     * 추이차트를 새로고침한다.
     * @return {[type]} [description]
     * @author jda
     */
    onClickRefreshButton() {
      this.$refs.flowChart.resetChart();
    },
    /**
     * 스마트 추적 컴포넌트를 로드하기 위한 파라메터를 가공한다.
     * @param  {[type]} data [description]
     * @return {[type]}      [description]
     * @author jda
     */
    setSmartTraceParams(data) {
      const flowParams = Object.assign({}, this.smartTraceParams, this.flowChartParams);

      if (!isEmpty(data.datetimeParams)) {
        Object.assign(flowParams.requestParamVO, data.datetimeParams);
      } else if (data.visibleInfoList) {
        if (data.visibleInfoList.length < 1) {
          this.smartTraceParams = null;
          return;
        }
        const type = this.analysisFilterName;
        const visibleSeries = [];

        // 그룹별 분석은 groupFilter사용, 나머지는 analysisFilter 사용.
        const analysisType = this.analysisType;
        if (analysisType === 'netinterfaceGrp' || analysisType === 'ipaddressGrp') {
          data.visibleInfoList.forEach((record) => {
            visibleSeries.push(record.info[0]);
          });
          flowParams.requestParamVO.groupFilter[type] = visibleSeries;
        } else {
          const visibleName = {};
          data.visibleInfoList.forEach((record) => {
            const id = find(record.info, { type }).value;
            visibleSeries.push(id);
            visibleName[id] = record.parentType;
          });

          flowParams.requestParamVO.analysisFilter[type] = visibleSeries;
          Object.assign(flowParams, { analysisFilterMap: visibleName, analysisFilterName: type });
        }
      }
      this.smartTraceParams = flowParams;
    },
    /**
     * 트래픽 목록 조회할 파라메터를 가공한다.
     * @param  {[type]} smartTraceFilter [description]
     * @author jda
     */
    setSmartTraceFilter(smartTraceList) {
      const smartTraceFilter = {};
      smartTraceList.forEach((info) => {
        if (info.selected) {
          smartTraceFilter[info.type] = info.itemId;
        }
      });
      this.trafficParams = Object.assign({}, this.smartTraceParams, { smartTraceFilter, summarySmartTraceList: smartTraceList, direction: this.direction });
    },
  },
};
</script>

<style lang="scss" scoped>
$url: '../../../../../assets/img/';

.list-info {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 10px;
  padding: 0 5px;

  & > .select {
    padding: 4px 0;
  }
  .select > .title {
    padding-right: 12px;
    font-size: 14px;
    letter-spacing: -1px;
  }

  .custom-input {
    margin: 0 15px 0 4px;
    //width: 160px;
    &:first-child {
      margin-left: 0;
    }
  }

  .lists {
    display: flex;
    align-items: center;
    //width: calc(100% - 180px);

    & > li {
      margin-left: 14px;
      padding-left: 10px;
      background: url($url+'dot.png') no-repeat 0 9px;
      font-weight: 500;

      &:first-child {
        margin-left: 0;
      }
    }
    & > li > .title {
      display: inline-block;
      padding-right: 4px;
      font-size: 13px;
      letter-spacing: -1px;
    }
    .high {
      color: #1c53ca;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/views/performance/FlowAnalysis/components/view/FlowAnalysisView.vue